import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext.js';

const navItems = [
  {
    label: 'Quant Research',
    children: [
      { label: 'Research', to: '/research' },
      { label: 'Tools', to: '/tools' },
    ],
  },
  {
    label: 'Arden Academy',
    children: [
      { label: 'Courses', to: '/academy' },
      { label: 'Resources', to: '/resources' },
    ],
  },
  {
    label: 'Blog',
    to: '/blog',
  },
  {
    label: 'About Us',
    to: '/about',
  },
];

const Header = ({ isScrolled }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const { isLight, toggleTheme } = useTheme();

  const textColor = isLight ? 'text-gray-900' : 'text-white';
  const mutedColor = isLight ? 'text-gray-500' : 'text-white/50';
  const accentColor = isLight ? 'text-primary' : 'text-accent';
  const hoverAccent = isLight ? 'hover:text-primary' : 'hover:text-accent';
  const borderColor = isLight ? 'border-gray-200' : 'border-white/10';

  return (
    <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 px-4 py-6 sm:px-8 sm:py-8 md:px-12 md:py-10 ${
      isScrolled
        ? isLight
          ? 'bg-background-light/80 backdrop-blur-lg py-4 sm:py-6'
          : 'bg-background-dark/80 backdrop-blur-lg py-4 sm:py-6'
        : 'bg-transparent'
    }`}>
      <div className="flex items-center justify-between max-w-[1600px] mx-auto">
        <Link to="/" className="flex items-center gap-2 sm:gap-4">
          <div className="relative flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8">
            <div className={`absolute w-full h-full border rounded-full animate-spin-slow ${isLight ? 'border-gray-300' : 'border-white/20'}`}></div>
            <div className={`w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full ${isLight ? 'bg-gray-900' : 'bg-white'}`}></div>
          </div>
          <h2 className={`text-[10px] sm:text-xs font-semibold tracking-[0.3em] sm:tracking-[0.5em] uppercase ${textColor}`}>
            <span className="hidden sm:inline">The Arden Research</span>
          </h2>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navItems.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setOpenDropdown(item.label)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button className={`text-[11px] sm:text-xs font-mono uppercase tracking-widest transition-colors flex items-center gap-1 py-2 ${
                  openDropdown === item.label ? accentColor : `${mutedColor} ${hoverAccent}`
                }`}>
                  {item.label}
                  <span className={`material-symbols-outlined text-[10px] transition-transform ${openDropdown === item.label ? 'rotate-180' : ''}`}>
                    expand_more
                  </span>
                </button>

                {openDropdown === item.label && (
                  <div className="absolute top-full left-0 min-w-[180px] pt-2">
                    <div className={`backdrop-blur-lg ${borderColor} border rounded-lg py-2 shadow-xl ${
                      isLight ? 'bg-white/95' : 'bg-background-dark/95'
                    }`}>
                      {item.children.map((child) => (
                        <Link
                          key={child.to}
                          to={child.to}
                          onClick={() => setOpenDropdown(null)}
                          className={`block px-5 py-2.5 text-[11px] font-mono uppercase tracking-widest transition-colors ${mutedColor} ${hoverAccent} ${
                            isLight ? 'hover:bg-primary/5' : 'hover:bg-white/5'
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                className={`text-[11px] sm:text-xs font-mono uppercase tracking-widest transition-colors ${mutedColor} ${hoverAccent}`}
              >
                {item.label}
              </Link>
            )
          )}

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all border ${
              isLight
                ? 'bg-primary/5 border-primary/20 text-primary hover:bg-primary/10'
                : 'bg-accent/5 border-accent/20 text-accent hover:bg-accent/10'
            }`}
            title={isLight ? 'Switch to dark mode' : 'Switch to light mode'}
          >
            <span className="material-symbols-outlined text-sm">
              {isLight ? 'dark_mode' : 'light_mode'}
            </span>
          </button>
        </nav>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden flex flex-col gap-1.5 items-end focus:outline-none"
          onClick={() => { setIsMenuOpen(!isMenuOpen); setMobileExpanded(null); }}
        >
          <div className={`w-6 h-[1px] transition-all ${isLight ? 'bg-gray-600' : 'bg-white/60'} ${isMenuOpen ? 'rotate-45 translate-y-[3.5px]' : ''}`}></div>
          <div className={`w-4 h-[1px] transition-all ${isLight ? 'bg-gray-600' : 'bg-white/60'} ${isMenuOpen ? '-rotate-45 -translate-y-[3.5px] w-6' : ''}`}></div>
        </button>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className={`md:hidden absolute top-full left-0 w-full backdrop-blur-lg ${borderColor} border-t ${
          isLight ? 'bg-background-light/95' : 'bg-background-dark/95'
        }`}>
          <nav className="flex flex-col px-4 py-6 gap-2">
            {navItems.map((item) =>
              item.children ? (
                <div key={item.label}>
                  <button
                    onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)}
                    className={`w-full flex items-center justify-between text-xs font-mono uppercase tracking-widest transition-colors py-2 ${mutedColor} ${hoverAccent}`}
                  >
                    {item.label}
                    <span className={`material-symbols-outlined text-sm transition-transform ${mobileExpanded === item.label ? 'rotate-180' : ''}`}>
                      expand_more
                    </span>
                  </button>
                  {mobileExpanded === item.label && (
                    <div className={`pl-4 flex flex-col gap-1 border-l ml-1 ${borderColor}`}>
                      {item.children.map((child) => (
                        <Link
                          key={child.to}
                          to={child.to}
                          onClick={() => { setIsMenuOpen(false); setMobileExpanded(null); }}
                          className={`text-xs font-mono uppercase tracking-widest transition-colors py-2 ${
                            isLight ? 'text-gray-400 hover:text-primary' : 'text-white/40 hover:text-accent'
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => { setIsMenuOpen(false); setMobileExpanded(null); }}
                  className={`text-xs font-mono uppercase tracking-widest transition-colors py-2 ${mutedColor} ${hoverAccent}`}
                >
                  {item.label}
                </Link>
              )
            )}

            {/* Mobile Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`flex items-center gap-2 text-xs font-mono uppercase tracking-widest transition-colors py-2 ${mutedColor} ${hoverAccent}`}
            >
              <span className="material-symbols-outlined text-sm">
                {isLight ? 'dark_mode' : 'light_mode'}
              </span>
              {isLight ? 'Dark Mode' : 'Light Mode'}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
